import { FormGroup, FormControl, Validators } from '@angular/forms';
import { VipLevel } from '../constants/vip-levels';
import { Country } from '../dtos/responses/country/country';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { ExceptionsDTO } from '../dtos/exceptions-dto/exceptions-dto';
import { specialCharValidator } from '../../validators/registration-validators';
import { requiredValidator } from '../../validators/add-edit-rewards-validators';

/**
 * Interface defining the structure of form controls for the reward form
 */
export interface RewardFormControls {
  isRelatedToProduct: FormControl<boolean | null>;
  rewardType: FormControl<SelectorData | null>;
  relatedProduct: FormControl<SelectorData | null>;
  name: FormControl<string | null>;
  namePt: FormControl<string | null>;
  nameEs: FormControl<string | null>;
  productImage: FormControl<string | null>;
  redemptionLimit: FormControl<number | null>;
  rewardDiscount: FormControl<number | null>;
  rewardCost: FormControl<number | null>;
  rewardCode: FormControl<string | null>;
  extensionPeriod: FormControl<number | null>;
  businessLevels: FormControl<VipLevel[] | null>;
  vipLevels: FormControl<VipLevel[] | null>;
  // TODO: type region exceptions
  regionExceptions: FormControl<any[] | null>;
  rewardFamilies: FormControl<any[] | null>;
  startDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
  //   isActive: FormControl<boolean | null>;
}

export interface RewardFormSteps {
  step1: FormGroup<{
    isRelatedToProduct: FormControl<boolean | null>;
    rewardType: FormControl<SelectorData | null>;
    relatedProduct: FormControl<SelectorData | null>;
    name: FormControl<string | null>;
    namePt: FormControl<string | null>;
    nameEs: FormControl<string | null>;
    productImage: FormControl<string | null>;
    redemptionLimit: FormControl<number | null>;
    rewardDiscount: FormControl<number | null>;
    rewardCost: FormControl<number | null>;
    rewardCode: FormControl<string | null>;
    extensionPeriod: FormControl<number | null>;
    businessLevels: FormControl<number[] | null>;
    vipLevels: FormControl<VipLevel[] | null>;
    rewardFamilies: FormControl<any[] | null>;
  }>;
  step2: FormGroup<{
    regionExceptions: FormControl<ExceptionsDTO[] | null>;
  }>;
  step3: FormGroup<{
    startDate: FormControl<Date | null>;
    endDate: FormControl<Date | null>;
  }>;
}

/**
 * Class containing factory method to create a reward form
 */
export class RewardForm {
  /**
   * Creates a new FormGroup instance with reward form controls
   * @return {FormGroup<RewardFormSteps>} A typed form group for managing reward data
   */
  static createForm(): FormGroup<RewardFormSteps> {
    const controls: RewardFormControls = {
      isRelatedToProduct: new FormControl<boolean | null>(false, [
        Validators.required,
      ]),
      rewardType: new FormControl<SelectorData | null>(null, [
        Validators.required,
      ]),
      relatedProduct: new FormControl<SelectorData | null>(null),
      name: new FormControl('', [
        requiredValidator('reward-form.reward-title-validation-en'),
        Validators.maxLength(200),
        specialCharValidator(),
      ]),
      namePt: new FormControl('', [
        requiredValidator('reward-form.reward-title-validation-pt'),
        Validators.maxLength(200),
        specialCharValidator(),
      ]),
      nameEs: new FormControl('', [
        requiredValidator('reward-form.reward-title-validation-es'),
        Validators.maxLength(200),
        specialCharValidator(),
      ]),
      productImage: new FormControl(null),
      redemptionLimit: new FormControl(null, [
        Validators.min(1),
        Validators.max(999),
        Validators.maxLength(3),
        Validators.pattern(/^[1-9]\d*$/),
      ]),
      rewardDiscount: new FormControl(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(100),
        Validators.maxLength(3),
        Validators.pattern(/^[1-9]\d*$/),
      ]),
      rewardCost: new FormControl(null, [
        Validators.required,
        Validators.min(1000),
        Validators.max(100000),
        Validators.maxLength(6),
        Validators.pattern(/^[1-9]\d*$/),
      ]),
      rewardCode: new FormControl(null, [
        Validators.required,
        Validators.maxLength(5),
        specialCharValidator(),
      ]),
      extensionPeriod: new FormControl(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(3),
        Validators.maxLength(1),
        Validators.pattern(/^[1-9]\d*$/),
      ]),
      vipLevels: new FormControl<VipLevel[]>([]),
      businessLevels: new FormControl<number[]>([]),
      regionExceptions: new FormControl<Country[] | null>(null),
      rewardFamilies: new FormControl<any[] | null>(null),
      startDate: new FormControl(new Date(), [Validators.required]),
      endDate: new FormControl(null),
    };

    return new FormGroup<RewardFormSteps>({
      step1: new FormGroup({
        isRelatedToProduct: controls.isRelatedToProduct,
        rewardType: controls.rewardType,
        relatedProduct: controls.relatedProduct,
        name: controls.name,
        namePt: controls.namePt,
        nameEs: controls.nameEs,
        productImage: controls.productImage,
        redemptionLimit: controls.redemptionLimit,
        rewardDiscount: controls.rewardDiscount,
        rewardCost: controls.rewardCost,
        rewardCode: controls.rewardCode,
        extensionPeriod: controls.extensionPeriod,
        rewardFamilies: controls.rewardFamilies,
        businessLevels: controls.businessLevels,
        vipLevels: controls.vipLevels,
      }),
      step2: new FormGroup({
        regionExceptions: controls.regionExceptions,
      }),
      step3: new FormGroup({
        startDate: controls.startDate,
        endDate: controls.endDate,
      }),
    });
  }
}
